import Button from "@/components/Button";
import useModal from "@/hooks/useModal";
import { useState, useEffect } from "react";
import { DateValueType } from "react-tailwindcss-datepicker";
import PATModalContent, { PATModalContentProps, PATSubmission } from "./PATModalContent";
import { usePATMutations } from "./usePATMutations";
import { isPATExemptTourValue } from "../../utils/pat";

// Map patType from submission to form type
const getFormType = (patType: string): string => {
  switch (patType) {
    case "HandicapSubmission":
    case "handicap_submission":
      return "handicap_submission";
    case "TourExemption":
    case "tour_exemption":
      return "tour_exemption";
    case "AlternateEvent":
    case "alternate_event":
      return "alternate_event";
    case "CombinedEvents":
    case "combined_events":
      return "combined_events";
    default:
      return "";
  }
};

// Format date for DateSelect component
const formatDateForDateSelect = (dateString: string): DateValueType | string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (!isNaN(date.getTime())) {
    return {
      startDate: date,
      endDate: date
    };
  }
  return dateString;
};

interface PATActivityModalProps {
  onDismiss: () => void;
  pgaId: string;
  personId?: string; // Required for add mode
  submission?: PATSubmission; // Required for edit mode
}

export default function PATActivityModal({ onDismiss, pgaId, personId, submission }: PATActivityModalProps) {
  const { Modal, close } = useModal();
  const isEditMode = !!submission;

  // Initialize form state
  const [type, setType] = useState<string>(submission ? getFormType(submission.patType) : "");
  const [handicapScore, setHandicapScore] = useState<string>(submission?.handicap?.toString() || "");
  const [reason, setReason] = useState<string>(submission?.tour || "");
  const [notes, setNotes] = useState<string>(submission?.note || "");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUploadErrorMsg, setFileUploadErrorMsg] = useState<string>("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState<string>("");
  const [eventName, setEventName] = useState<string>(submission?.name || "");
  const [eventDate, setEventDate] = useState<DateValueType | string>(
    submission?.date ? formatDateForDateSelect(submission.date) : ""
  );
  const [first18HoleScore, setFirst18HoleScore] = useState<string>(submission?.roundOneScore?.toString() || "");
  const [second18HoleScore, setSecond18HoleScore] = useState<string>(submission?.roundTwoScore?.toString() || "");
  const [targetScore, setTargetScore] = useState<string>(submission?.targetScore?.toString() || "");

  // Ensure eventDate is properly formatted if submission date changes
  useEffect(() => {
    if (submission?.date && typeof eventDate === "string") {
      setEventDate(formatDateForDateSelect(submission.date));
    }
  }, [submission?.date, eventDate]);

  useEffect(() => {
    if (Object.keys(fieldErrors).length > 0) {
      setTimeout(() => {
        const modalElement = document.querySelector("md-dialog");
        const scroller = modalElement?.shadowRoot?.querySelector(".scroller") as HTMLElement;
        if (scroller) {
          scroller.scrollTo({
            top: 0,
            behavior: "smooth"
          });
        }
      }, 100);
    }
  }, [fieldErrors]);

  const clearFieldError = (fieldName: string) => {
    setFieldErrors((prev) => {
      const updated = { ...prev };
      delete updated[fieldName];
      return updated;
    });
  };

  const {
    createHandicapSubmission,
    createExemption,
    createAlternateEvent,
    updateHandicapSubmission,
    updateExemption,
    updateAlternateEvent,
    loading
  } = usePATMutations({
    pgaId,
    onSuccess: () => {
      close();
      onDismiss();
    },
    onError: (errorMessage) => {
      setApiError(errorMessage);
    }
  });

  const handleSave = async () => {
    setFieldErrors({});
    setApiError("");

    const errors: Record<string, string> = {};

    if (!type) {
      errors.type = "Please select a valid PAT type";
    }

    if (type === "handicap_submission") {
      if (!handicapScore) {
        errors.handicapScore = "Handicap score is required";
      } else {
        const handicap = parseFloat(handicapScore);
        if (isNaN(handicap)) {
          errors.handicapScore = "Handicap score must be a valid number";
        }
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      const handicap = parseFloat(handicapScore);

      try {
        if (isEditMode && submission) {
          await updateHandicapSubmission({
            variables: {
              input: {
                id: submission.id,
                handicap,
                ...(notes && { note: notes }),
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        } else if (personId) {
          await createHandicapSubmission({
            variables: {
              input: {
                personId,
                handicap,
                ...(notes && { note: notes }),
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        }
      } catch (err) {}
    } else if (type === "tour_exemption") {
      if (!reason) {
        setFieldErrors({ reason: "Reason is required" });
        return;
      }

      if (!isPATExemptTourValue(reason)) {
        setFieldErrors({ reason: "Please select a valid reason" });
        return;
      }

      try {
        if (isEditMode && submission) {
          await updateExemption({
            variables: {
              input: {
                id: submission.id,
                tour: reason,
                ...(notes && { note: notes }),
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        } else if (personId) {
          await createExemption({
            variables: {
              input: {
                personId,
                tour: reason,
                ...(notes && { note: notes }),
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        }
      } catch (err) {}
    } else if (type === "alternate_event" || type === "combined_events") {
      if (!eventName) {
        errors.eventName = "Event name is required";
      }
      if (!eventDate || (typeof eventDate === "object" && !eventDate.startDate)) {
        errors.eventDate = "Event date is required";
      }
      if (!first18HoleScore) {
        errors.first18HoleScore = "First 18 hole score is required";
      }
      if (!second18HoleScore) {
        errors.second18HoleScore = "Second 18 hole score is required";
      }
      if (!targetScore) {
        errors.targetScore = "Target score is required";
      }
      if (!notes) {
        errors.notes = "Details is required";
      }

      if (first18HoleScore) {
        const firstScore = parseInt(first18HoleScore, 10);
        if (isNaN(firstScore)) {
          errors.first18HoleScore = "First 18 hole score must be a valid number";
        }
      }
      if (second18HoleScore) {
        const secondScore = parseInt(second18HoleScore, 10);
        if (isNaN(secondScore)) {
          errors.second18HoleScore = "Second 18 hole score must be a valid number";
        }
      }
      if (targetScore) {
        const target = parseInt(targetScore, 10);
        if (isNaN(target)) {
          errors.targetScore = "Target score must be a valid number";
        }
      }

      if (eventDate && typeof eventDate === "object" && eventDate.startDate) {
        const dateValue = eventDate.startDate;
        const formattedDate =
          dateValue instanceof Date
            ? dateValue.toISOString().split("T")[0]
            : typeof dateValue === "string"
              ? new Date(dateValue).toISOString().split("T")[0]
              : "";

        if (!formattedDate) {
          errors.eventDate = "Invalid date format";
        }
      }

      if (
        first18HoleScore &&
        second18HoleScore &&
        targetScore &&
        !errors.first18HoleScore &&
        !errors.second18HoleScore &&
        !errors.targetScore
      ) {
        const firstScore = parseInt(first18HoleScore, 10);
        const secondScore = parseInt(second18HoleScore, 10);
        const target = parseInt(targetScore, 10);
        const totalScore = firstScore + secondScore;

        if (totalScore > target) {
          errors.targetScore = "Total score exceeds target score. This score is not passing.";
        }
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }

      const firstScore = parseInt(first18HoleScore, 10);
      const secondScore = parseInt(second18HoleScore, 10);
      const target = parseInt(targetScore, 10);
      const dateValue =
        eventDate && typeof eventDate === "object" && eventDate.startDate ? eventDate.startDate : eventDate || "";
      const formattedDate =
        dateValue instanceof Date
          ? dateValue.toISOString().split("T")[0]
          : typeof dateValue === "string"
            ? new Date(dateValue).toISOString().split("T")[0]
            : "";

      try {
        if (isEditMode && submission) {
          await updateAlternateEvent({
            variables: {
              input: {
                id: submission.id,
                eventName,
                eventDate: formattedDate,
                first18HoleScore: firstScore,
                second18HoleScore: secondScore,
                targetScore: target,
                combinedEvents: type === "combined_events",
                note: notes,
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        } else if (personId) {
          await createAlternateEvent({
            variables: {
              input: {
                personId,
                eventName,
                eventDate: formattedDate,
                first18HoleScore: firstScore,
                second18HoleScore: secondScore,
                targetScore: target,
                combinedEvents: type === "combined_events",
                note: notes,
                ...(selectedFile && {
                  document: {
                    name: selectedFile.name,
                    file: selectedFile
                  }
                })
              }
            }
          });
        }
      } catch (err) {}
    } else if (type) {
      errors.type = "Please select a valid PAT type";
      setFieldErrors(errors);
    }
  };

  const patModalContentProps: PATModalContentProps = {
    operation: isEditMode ? "edit" : "add",
    submission,
    apiError,
    fieldErrors,
    clearFieldError,
    patType: type,
    setPatType: setType,
    handicapScore,
    setHandicapScore,
    reason,
    setReason,
    eventName,
    setEventName,
    eventDate,
    setEventDate,
    first18HoleScore,
    setFirst18HoleScore,
    second18HoleScore,
    setSecond18HoleScore,
    targetScore,
    setTargetScore,
    notes,
    setNotes,
    selectedFile,
    setSelectedFile,
    fileUploadErrorMsg,
    setFileUploadErrorMsg
  };

  return (
    <Modal
      onClosed={onDismiss}
      action={
        <Button
          onClick={handleSave}
          disabled={
            loading ||
            (type !== "handicap_submission" &&
              type !== "tour_exemption" &&
              type !== "alternate_event" &&
              type !== "combined_events")
          }
        >
          Save
        </Button>
      }
      headline={isEditMode ? "Edit PAT Activity" : "Add PAT Activity"}
      className={type ? "max-h-[85vh]" : ""}
    >
      <PATModalContent {...patModalContentProps} />
    </Modal>
  );
}
